import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Zap, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface AIBuilderScreenProps {
  onNext: () => void;
  onBack: () => void;
}
export function AIBuilderScreen({
  onNext,
  onBack
}: AIBuilderScreenProps) {
  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState<'research' | 'quick'>('research');
  const suggestions = ["I'm a coach and want to book more calls", 'I created a course and want to launch it', 'I sell products and need more traffic'];
  const handleSuggestionClick = (suggestion: string) => {
    setPrompt(suggestion);
  };
  return <div className="min-h-screen flex flex-col p-6 pb-20 bg-[#0f0f2d]" data-id="element-56">
      <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full pt-12" data-id="element-57">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex items-start justify-between mb-8" data-id="element-58">
          <div data-id="element-59">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-3" data-id="element-60">
              What do you want to achieve?
            </h2>
            <p className="text-[#8b8bab] text-lg" data-id="element-61">
              ClickFunnels AI will research your market and design a custom
              funnel in minutes
            </p>
          </div>

          {/* Labs Badge */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.2
        }} className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#2a2a5a] bg-[#1a1a3e]/50 text-white text-sm font-medium" data-id="element-62">
            <div size={16} data-id="element-63" />
            Labs
          </motion.div>
        </motion.div>

        {/* Main Input Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-[#1a1a3e]/60 border border-[#2a2a5a] rounded-2xl p-6 mb-6" data-id="element-64">
          {/* Textarea */}
          <textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Tell us about your business and what you want to achieve..." className="w-full h-40 bg-transparent text-white text-lg placeholder-[#5a5a7a] resize-none focus:outline-none" data-id="element-65" />

          {/* Suggestion Pills */}
          <div className="flex flex-wrap gap-3 mt-4 mb-6" data-id="element-66">
            {suggestions.map((suggestion, idx) => <motion.button key={idx} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2 + idx * 0.1
          }} onClick={() => handleSuggestionClick(suggestion)} className={`px-4 py-2.5 rounded-full border text-sm transition-all
                  ${prompt === suggestion ? 'border-[#e879a9] bg-[#e879a9]/10 text-[#e879a9]' : 'border-[#3a3a6a] text-[#8b8bab] hover:border-[#5a5a7a] hover:text-white'}`} data-id="element-67">
                {suggestion}
              </motion.button>)}
          </div>

          {/* Mode Toggle */}
          <div className="flex items-center gap-4" data-id="element-68">
            <div className="flex items-center bg-[#0f0f2d] rounded-full p-1" data-id="element-69">
              <button onClick={() => setMode('research')} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all
                  ${mode === 'research' ? 'bg-gradient-to-r from-[#e879a9] to-[#c084fc] text-white' : 'text-[#8b8bab] hover:text-white'}`} data-id="element-70">
                <Search size={16} data-id="element-71" />
                Research
              </button>
              <button onClick={() => setMode('quick')} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all
                  ${mode === 'quick' ? 'bg-gradient-to-r from-[#e879a9] to-[#c084fc] text-white' : 'text-[#8b8bab] hover:text-white'}`} data-id="element-72">
                <Zap size={16} data-id="element-73" />
                Quick
              </button>
            </div>

            <div className="flex items-center gap-2 text-sm" data-id="element-74">
              <Search size={14} className="text-[#5a5a7a]" data-id="element-75" />
              <span className="text-white font-medium" data-id="element-76">
                {mode === 'research' ? 'Research mode' : 'Quick mode'}
              </span>
              <span className="text-[#5a5a7a]" data-id="element-77">
                —{' '}
                {mode === 'research' ? 'AI analyzes your market first' : 'AI builds your funnel instantly'}
              </span>
            </div>
          </div>
        </motion.div>

        {/* CTA Button */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.4
      }} className="flex items-center justify-end" data-id="element-78">
          <motion.button whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} onClick={onNext} disabled={!prompt.trim()} className={`flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-lg transition-all
              ${prompt.trim() ? 'bg-gradient-to-r from-[#e879a9] to-[#c084fc] text-white shadow-lg shadow-[#e879a9]/20 hover:shadow-[#e879a9]/30' : 'bg-[#1a1a3e] text-[#5a5a7a] cursor-not-allowed'}`} data-id="element-79">
            Let's build
            <ArrowRight size={20} data-id="element-80" />
          </motion.button>
        </motion.div>
      </div>

      {/* Back Button - Centered at Bottom */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.5
    }} className="flex justify-center pt-8" data-id="element-81">
        <button onClick={onBack} className="flex items-center gap-2 text-[#5a5a7a] hover:text-white text-sm font-medium transition-colors" data-id="element-82">
          <ArrowLeft size={16} data-id="element-83" />
          Back
        </button>
      </motion.div>
    </div>;
}